import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import privateStyles from './PrivateBadge.module.css';
import styles from './VoiceDisclosure.module.css';

interface VoiceDisclosureProps {
  /** Called when the user confirms and voice recording should start. */
  onConfirm: () => void;
  /**
   * Called when the user dismisses without confirming (Escape or backdrop).
   * The "seen" flag is still set — showing the disclosure once is enough
   * even if the user chose not to record this time.
   */
  onDismiss: () => void;
}

/**
 * One-time first-tap disclosure shown before a user's first voice
 * recording. Names what the transcription service does and does not do,
 * in the app's reverent tone. Reuses PrivateBadge's backdrop so it
 * inherits the iOS Safari stacking fix (z-index: 9999 + translateZ(0));
 * any modal in this app that sits over PrayPage needs that treatment.
 */
export default function VoiceDisclosure({ onConfirm, onDismiss }: VoiceDisclosureProps) {
  const { t } = useTranslation();

  useEffect(() => {
    // Dismiss the iOS on-screen keyboard if it's up — the textarea that
    // triggered the mic tap (PrayPage main, or the active UpdateSheet)
    // keeps focus otherwise, and the keyboard can cover the confirm
    // button on shorter phones.
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onDismiss]);

  return (
    <div
      className={privateStyles.backdrop}
      onClick={onDismiss}
      role="presentation"
    >
      <div
        className={`${privateStyles.popover} ${styles.popover}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="voice-disclosure-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="voice-disclosure-title" className={styles.title}>
          {t('voice.disclosureTitle')}
        </h2>
        <p className={styles.lead}>{t('voice.disclosureLead')}</p>
        <ul className={styles.list}>
          <li>{t('voice.disclosureBullet1')}</li>
          <li>{t('voice.disclosureBullet2')}</li>
          <li>{t('voice.disclosureBullet3')}</li>
          <li>{t('voice.disclosureBullet4')}</li>
          <li>{t('voice.disclosureBullet5')}</li>
        </ul>
        <button
          type="button"
          className={privateStyles.popoverClose}
          onClick={onConfirm}
        >
          {t('voice.disclosureConfirm')}
        </button>
      </div>
    </div>
  );
}
